import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from '@mui/material';
import { WorkoutSet } from '@strength/domain-workout';
import { MdClose } from '@teamworksdev/icons';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterOutput } from '../../../../../../trpc';
import { SearchInput } from '../../../../../components/input/search-input';
import { cn } from '../../../../../components/utils';
import { useMetrics } from '../../../../../hooks/useMetrics';
import {
  groupMetricsByCategory,
  searchMetrics,
} from '../../../../../utils/metric-categories';
import { useWorkoutContext } from '../../context/WorkoutContext';
import { CreateEditExerciseMetrics } from './types';
import { getNumOfSetsForNewMetric } from './utils';

type Metric = RouterOutput['metricsLibrary']['metricsSearch']['records'][0];

type AddMetricsModalProps = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSave: (metrics: CreateEditExerciseMetrics[]) => void;
  selectedMetrics: CreateEditExerciseMetrics[];
  sets?: WorkoutSet[];
};

export const AddMetricsModal = ({
  isOpen,
  onOpen,
  onClose,
  onSave,
  selectedMetrics,
  sets,
}: AddMetricsModalProps) => {
  const { t } = useTranslation();
  const [selectedMetricEdits, setSelectedMetricEdits] =
    useState<CreateEditExerciseMetrics[]>(selectedMetrics);

  useEffect(() => {
    setSelectedMetricEdits(selectedMetrics);
  }, [selectedMetrics]);

  const { getMetrics, categories } = useMetrics(false);
  const { getUnitsForMetric } = useWorkoutContext();
  const [search, setSearch] = useState('');
  const metrics = useMemo(() => {
    return getMetrics();
  }, [getMetrics]);

  const grouped = categories.length > 0;
  const displayGroups = useMemo(() => {
    if (!grouped) {
      return null;
    }
    return groupMetricsByCategory(
      searchMetrics(metrics, categories, search),
      categories,
    );
  }, [grouped, metrics, categories, search]);

  const renderMetric = (metric: (typeof metrics)[number], label: ReactNode) => {
    const isSelected = selectedMetricEdits.some(
      (m) => m.metricId === metric.id,
    );
    return (
      <div
        key={metric.id}
        className={cn(isSelected && 'bg-gray-100 rounded', 'cursor-pointer')}
        onClick={() => handleMetricSelect(metric, isSelected)}
      >
        <Checkbox
          checked={isSelected}
          onChange={() => handleMetricSelect(metric, isSelected)}
          inputProps={{ 'aria-label': metric.name }}
        />
        {label}
      </div>
    );
  };

  const handleMetricSelect = (metric: Metric, isSelected: boolean) => {
    if (isSelected) {
      setSelectedMetricEdits((prev) =>
        prev.filter((m) => m.metricId !== metric.id),
      );
    } else {
      setSelectedMetricEdits((prev) => {
        if (prev.some((m) => m.metricId === metric.id)) {
          return prev;
        }

        const setIds = sets?.map((set) => set.workoutSetId) ?? [];
        const defaultUnit = getUnitsForMetric(metric.id)?.[0] ?? '';
        // Derive from the current edit state (prev), not the original prop, so a
        // new metric falls back to the set count once all metrics are removed.
        const numOfSets = getNumOfSetsForNewMetric(prev, sets?.length ?? 0);

        return [
          ...prev,
          {
            metricId: metric.id,
            name: metric.name,
            type: metric.type,
            sortOrder: metric.sortOrder,
            inputsForSet: Array.from({ length: numOfSets }, (_, i) => ({
              setIndex: i,
              value: '0',
              unit: defaultUnit,
              setId: setIds[i] ?? '',
            })),
          },
        ];
      });
    }
  };

  return (
    <div>
      <Button variant="outlined" onClick={onOpen}>
        {t('editMetrics')}
      </Button>
      <Dialog
        key={'add-edit-modal'}
        onClose={onClose}
        open={isOpen}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '500px',
          },
        }}
      >
        <DialogTitle className="flex flex-row justify-between items-center">
          {t('editMetrics')}
          <IconButton onClick={onClose}>
            <MdClose size={24} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            // Grouped mode keeps the search pinned above the list, so the
            // list scrolls in its own container instead of DialogContent.
            ...(grouped && { overflow: 'hidden' }),
          }}
        >
          {grouped && (
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('searchMetrics')}
              showLabel={false}
              fullWidth
            />
          )}
          {(() => {
            const list = displayGroups ? (
              displayGroups.length === 0 ? (
                // Announced to screen readers as results change while typing
                <div role="status">{t('noMetricsFound')}</div>
              ) : (
                displayGroups.map((group) =>
                  group.metrics.map((metric) =>
                    renderMetric(
                      metric,
                      group.category ? (
                        <>
                          <span className="text-muted-foreground">
                            {group.category.name} &gt;
                          </span>
                          <span className="ml-1">{metric.name}</span>
                        </>
                      ) : (
                        metric.name
                      ),
                    ),
                  ),
                )
              )
            ) : (
              metrics?.map((metric) => renderMetric(metric, metric.name))
            );
            return grouped ? (
              <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
                {list}
              </div>
            ) : (
              list
            );
          })()}
        </DialogContent>
        <Divider />
        <DialogActions className="p-6">
          <Button variant="outlined" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              onSave(
                selectedMetricEdits.sort((a, b) => a.sortOrder - b.sortOrder),
              );
              onClose();
            }}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
